import { useEffect } from 'react';

export interface KeyboardShortcutHandlers {
  /** Cmd/Ctrl+K — focus the session search. */
  onFocusSearch?: () => void;
  /** Cmd/Ctrl+Shift+O — start a new chat. */
  onNewChat?: () => void;
  /** Escape — stop an in-flight generation. */
  onStopGeneration?: () => void;
  /** Whether a generation is running (gates the Escape handler). */
  isGenerating?: boolean;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * Application keyboard shortcuts.
 *
 * Deliberately small and non-clashing: shortcuts that fire while the user is
 * typing a requirement would be worse than having none at all, so everything
 * except the explicitly global chords is suppressed inside text fields.
 */
export function useKeyboardShortcuts({
  onFocusSearch,
  onNewChat,
  onStopGeneration,
  isGenerating = false,
}: KeyboardShortcutHandlers): void {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const mod = event.metaKey || event.ctrlKey;
      const key = event.key.toLowerCase();

      // Global chords work everywhere, including inside the composer.
      if (mod && !event.altKey && key === 'k') {
        if (!onFocusSearch) return;
        event.preventDefault();
        onFocusSearch();
        return;
      }

      if (mod && event.shiftKey && key === 'o') {
        if (!onNewChat) return;
        event.preventDefault();
        onNewChat();
        return;
      }

      // Escape stops a running generation, but never steals Escape from a
      // field the user is editing (dropdowns and dialogs need it there).
      if (key === 'escape' && isGenerating && onStopGeneration && !isTypingTarget(event.target)) {
        event.preventDefault();
        onStopGeneration();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isGenerating, onFocusSearch, onNewChat, onStopGeneration]);
}
